import React, { useState } from 'react';
import { Table, Button, Input, Space, Tag } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import './UserManagementPage.css';

interface User {
  id: string;
  stt: number;
  username: string;
  fullName: string;
  email: string;
  role: string;
  medicalFacility: string;
  status: 'active' | 'inactive';
}

const UserManagementPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');

  // Mock data
  const mockData: User[] = [
    {
      id: '1',
      stt: 1,
      username: 'user1',
      fullName: 'Nguyễn Văn A',
      email: 'user1@example.com',
      role: 'admin_medical_facility',
      medicalFacility: 'Chuỗi Omecap template',
      status: 'active',
    },
    {
      id: '2',
      stt: 2,
      username: 'user2',
      fullName: 'Trần Thị B',
      email: 'user2@example.com',
      role: 'admin_medical_facility',
      medicalFacility: 'Phòng khám Sản phụ khoa',
      status: 'active',
    },
    {
      id: '3',
      stt: 3,
      username: 'user3',
      fullName: 'Lê Văn C',
      email: 'user3@example.com',
      role: 'admin_medical_facility',
      medicalFacility: 'Nguyễn Thị Huyền',
      status: 'inactive',
    },
  ];

  const columns: ColumnsType<User> = [
    {
      title: 'STT',
      dataIndex: 'stt',
      key: 'stt',
      width: 60,
      align: 'center',
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: 'Họ và tên',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 180,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      render: (role: string) => (
        <Tag color={role === 'admin_medical_facility' ? 'blue' : 'default'}>
          {role === 'admin_medical_facility' ? 'Quản lý CSYT' : role}
        </Tag>
      ),
    },
    {
      title: 'Cơ sở y tế',
      dataIndex: 'medicalFacility',
      key: 'medicalFacility',
      width: 250,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      ),
    },
    {
      title: 'Tùy chọn',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: () => (
        <Space>
          <Button type="link" icon={<EditOutlined />} />
          <Button type="link" icon={<EyeOutlined />} />
          <Button type="link" icon={<DeleteOutlined />} danger />
        </Space>
      ),
    },
  ];

  const filteredData = mockData.filter((item) =>
    item.username.toLowerCase().includes(searchText.toLowerCase()) ||
    item.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
    item.email.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="user-management-page">
      <div className="page-header">
        <h1>Quản lý user</h1>
        <Button type="primary" icon={<PlusOutlined />} size="large">
          Tạo mới user
        </Button>
      </div>
      <div className="page-toolbar">
        <Input
          placeholder="Tìm kiếm"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        pagination={{
          current: 1,
          pageSize: 10,
          total: filteredData.length,
          showSizeChanger: false,
        }}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default UserManagementPage;
